// Read-only renderers shared by the editor's review step and the permit page view.
import type { ReactNode } from 'react'
import { Badge, Button, Card, KeyValue, SectionTitle } from '@/components/ui'
import { GAS_SPECIES, GAS_TRIGGERS, ISOLATION_KINDS, PERMIT_CLASSES } from '@/domain/catalogue'
import type { AreaClassification, AreaRef, DraftFields, DraftTask, PermitBundle, Registry, RevisionTask } from '@/domain/types'
import { fmtDateTime, hoursText, labelFor } from '@/lib/format'
import { StatusBadge, SubHeading, TextBlock } from './bits'
import type { StepKey } from './model'
import {
  CLASSIFICATION_FIELDS,
  classificationValue,
  missingClassification,
  PROCEDURE_STATUS,
  ROUTE_QUESTIONS,
  speciesLabel,
  classActivityLabels,
  JOB_READINESS_CONFIRMATIONS,
  JOB_READINESS_QUESTION,
  locationClassificationLabel,
  taskIsPressureTest,
  THIRD_PARTY_CONFIRMATIONS,
  THIRD_PARTY_QUESTION,
  windowSpan,
  yesNo,
} from './model'

// -----------------------------------------------------------------------------
// Lookups from ids to names (registry first, then what the permit bundle carries)
// -----------------------------------------------------------------------------
type AreaInfo = AreaRef & Partial<AreaClassification> & { location_classification?: string | null; location_classification_label?: string | null }

export type Lookup = {
  person: (id: string | null) => { display_name: string; organisation: string | null } | null
  area: (id: string | null) => AreaInfo | null
  workType: (id: string | null) => { code: string; description: string; activity_group: string } | null
  equipment: (id: string | null) => { tag: string; description: string; site_area_id: string } | null
  siteName: (id: string | null) => string | null
}

export function makeLookup(registry: Registry | null | undefined, bundle?: PermitBundle | null): Lookup {
  return {
    person: (id) => {
      if (!id) return null
      const p = registry?.people.find((x) => x.id === id)
      if (p) return p
      for (const r of bundle?.revisions ?? []) {
        const party = r.parties.find((x) => x.person.id === id)
        if (party) return party.person
      }
      return null
    },
    area: (id) => {
      if (!id) return null
      for (const s of registry?.sites ?? []) {
        const a = s.areas.find((x) => x.id === id)
        if (a) return a
      }
      return bundle?.permit.site_area.id === id ? bundle.permit.site_area : null
    },
    workType: (id) => {
      if (!id) return null
      return registry?.work_types.find((w) => w.id === id) ?? (bundle?.permit.work_type.id === id ? bundle.permit.work_type : null)
    },
    equipment: (id) => (id ? (registry?.equipment.find((e) => e.id === id) ?? null) : null),
    siteName: (id) => {
      if (!id) return null
      return registry?.sites.find((s) => s.id === id)?.name ?? (bundle?.permit.site.id === id ? bundle.permit.site.name : null)
    },
  }
}

export function classLabel(code: string | null | undefined): string {
  if (!code) return 'Not chosen'
  return PERMIT_CLASSES.find((c) => c.code === code)?.label ?? `Class ${code}`
}

// -----------------------------------------------------------------------------
// Area classification snapshot
// -----------------------------------------------------------------------------
export function ClassificationList({ area, note }: { area: object | null | undefined; note?: ReactNode }) {
  if (!area) return <p className="text-sm text-ink-faint">No area chosen.</p>
  const a = area as Record<string, unknown>
  const synthetic = a.classification_status === 'synthetic_fixture'
  const missing = missingClassification(area)
  return (
    <div className="space-y-2">
      {synthetic ? (
        <p className="flex flex-wrap items-center gap-2 text-xs text-synthetic">
          <Badge tone="synthetic">synthetic</Badge>
          Synthetic fixture classification - not from a real hazardous-area drawing or safety case.
        </p>
      ) : null}
      <KeyValue items={CLASSIFICATION_FIELDS.map(([key, label]) => [label, classificationValue(key, a[key])] as [ReactNode, ReactNode])} />
      {missing.length > 0 || a.classification_status === 'unresolved' ? (
        <p className="text-xs font-semibold text-bad">
          <span aria-hidden>! </span>
          {missing.length > 0 ? `Not recorded: ${missing.join(', ')}. ` : ''}
          The register classification is incomplete or unresolved (CORE-031).
        </p>
      ) : null}
      {note}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Tasks
// -----------------------------------------------------------------------------
export type TaskDisplay = {
  sequence: number
  equipment: string | null
  description: string
  /** Pre-0015 revisions only: the retired task boundary (blank from 0015). */
  boundary: string
  activity_group: string | null
  /** True for a Pressure Testing task (or a pre-0015 task answered yes). */
  is_pressure_test: boolean
  pressure_test_type: string | null
  test_medium: string | null
  surface_preparation_method: string | null
  procedure: { identity: string; revision: string; issuer: string } | null
  /** Pre-0015 revisions only: the retired evidence references. */
  evidence_refs: string[]
  verification_status?: string
}

export function taskFromDraft(t: DraftTask): TaskDisplay {
  const ref = t.applicable_procedure_reference
  return {
    sequence: t.sequence,
    equipment: t.equipment_text,
    description: t.description,
    boundary: '',
    activity_group: t.activity_group,
    is_pressure_test: taskIsPressureTest(t),
    pressure_test_type: t.pressure_test_type,
    test_medium: t.test_medium,
    surface_preparation_method: t.surface_preparation_method,
    procedure: ref && (ref.identity || ref.revision || ref.issuer) ? ref : null,
    evidence_refs: [],
  }
}

export function taskFromRevision(t: RevisionTask): TaskDisplay {
  const hasRef = Boolean(t.procedure_identity || t.procedure_revision || t.procedure_issuer)
  return {
    sequence: t.sequence,
    equipment: t.equipment ? `${t.equipment.tag} - ${t.equipment.description}` : t.equipment_text,
    description: t.description,
    boundary: t.boundary ?? '',
    activity_group: t.activity_group,
    is_pressure_test: t.is_pressure_test === true || taskIsPressureTest(t),
    pressure_test_type: t.pressure_test_type,
    test_medium: t.test_medium,
    surface_preparation_method: t.surface_preparation_method,
    procedure: hasRef ? { identity: t.procedure_identity ?? '', revision: t.procedure_revision ?? '', issuer: t.procedure_issuer ?? '' } : null,
    evidence_refs: t.procedure_evidence_refs ?? [],
    verification_status: t.procedure_verification_status,
  }
}

export function procedureText(p: TaskDisplay['procedure']): string {
  if (!p) return ''
  return `${p.identity || '(no identifier)'} revision ${p.revision || '(none)'}, issued by ${p.issuer || '(not stated)'}`
}

export function TaskCard({ task }: { task: TaskDisplay }) {
  const items: Array<[ReactNode, ReactNode]> = [
    ['Equipment', <TextBlock text={task.equipment} empty="Not given" />],
    ['Activity group', task.activity_group ?? <span className="text-ink-faint">Not chosen</span>],
    ['What is done', <TextBlock text={task.description} />],
  ]
  // Retired fields are shown only where an earlier revision recorded them.
  if (task.boundary.trim()) items.push(['Task boundary (earlier form)', <TextBlock text={task.boundary} />])
  if (task.is_pressure_test) {
    items.push(['Pressure test type', task.pressure_test_type ?? <span className="text-ink-faint">Not chosen</span>])
    items.push(['Actual test medium', <TextBlock text={task.test_medium} empty="Not stated" />])
  }
  if (task.activity_group === 'Surface Preparation') {
    items.push(['Actual preparation method', <TextBlock text={task.surface_preparation_method} empty="Not stated" />])
  }
  items.push(['Procedure', task.procedure ? procedureText(task.procedure) : <span className="text-ink-faint">Not given - every task needs its procedure</span>])
  if (task.evidence_refs.length > 0) {
    items.push([
      'Procedure evidence (earlier form)',
      <ul className="list-inside list-disc">
        {task.evidence_refs.map((r, i) => (
          <li key={i} className="break-words">
            {r || <span className="text-ink-faint">(blank)</span>}
          </li>
        ))}
      </ul>,
    ])
  }
  return (
    <div className="rounded-lg border border-line bg-white p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold text-ink">Task {task.sequence}</p>
        <div className="flex flex-wrap gap-1">
          {task.is_pressure_test ? <Badge tone="info">Pressure test{task.pressure_test_type ? `: ${task.pressure_test_type}` : ''}</Badge> : null}
          {task.verification_status ? <StatusBadge status={PROCEDURE_STATUS[task.verification_status] ?? { label: task.verification_status, tone: 'neutral' }} /> : null}
        </div>
      </div>
      <KeyValue items={items} />
    </div>
  )
}

// -----------------------------------------------------------------------------
// Hazards, JHSEP, SIMOPS, special work, gas plan
// -----------------------------------------------------------------------------
export function ControlsList({ items }: { items: { hazard: string; control: string; kind: string | null }[] }) {
  if (items.length === 0) return <p className="text-sm text-ink-faint">No hazards listed.</p>
  return (
    <ol className="space-y-2">
      {items.map((h, i) => (
        <li key={i} className="rounded-lg border border-line bg-white p-2 text-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
            {i + 1}. {h.kind === 'additional' ? 'Additional control' : h.kind === 'standard' ? 'Standard control' : 'Control type not chosen'}
          </p>
          <p className="mt-1">
            <span className="font-semibold">Hazard: </span>
            <TextBlock text={h.hazard} />
          </p>
          <p className="mt-0.5">
            <span className="font-semibold">Control: </span>
            <TextBlock text={h.control} />
          </p>
        </li>
      ))}
    </ol>
  )
}

export function JhsepView({ jhsep }: { jhsep: DraftFields['jhsep'] | null }) {
  if (!jhsep) return <p className="text-sm text-ink-faint">No Job HSE plan recorded.</p>
  return (
    <KeyValue
      items={[
        ['Level', jhsep.level === 2 ? 'Level 2 - not built in this release' : jhsep.level === 1 ? 'Level 1' : <span className="text-ink-faint">Not answered</span>],
        ['Reference', <TextBlock text={jhsep.reference} />],
        ['Summary', <TextBlock text={jhsep.summary} />],
        ['MOPO reviewed', yesNo(jhsep.mopo_reviewed)],
        ['Lessons from incidents reviewed', yesNo(jhsep.lessons_reviewed)],
        ['Site examination done', yesNo(jhsep.site_examination_done)],
        ['Site examination note', <TextBlock text={jhsep.site_examination_note} empty="None" />],
      ]}
    />
  )
}

export function SimopsView({ simops }: { simops: DraftFields['simops'] }) {
  return (
    <KeyValue
      items={[
        ['Nearby activities', <TextBlock text={simops.nearby_activities} />],
        ['Conflicts identified', <TextBlock text={simops.conflicts_identified} />],
        ['MOPO consulted for SIMOPS', yesNo(simops.mopo_consulted)],
      ]}
    />
  )
}

export function RouteAnswers({ fields }: { fields: Pick<DraftFields, (typeof ROUTE_QUESTIONS)[number]['key']> }) {
  return (
    <ul className="divide-y divide-line rounded-lg border border-line bg-white">
      {ROUTE_QUESTIONS.map((q) => {
        const v = fields[q.key]
        return (
          <li key={q.key} className="flex flex-wrap items-center justify-between gap-2 p-2 text-sm">
            <span className="text-ink">{q.question}</span>
            {v === true && q.key === 'isolation_required' ? (
              <Badge tone="warn">Yes - details below</Badge>
            ) : v === true ? (
              <Badge tone="bad">Yes - not supported in this release</Badge>
            ) : v === false ? (
              <Badge tone="neutral">No</Badge>
            ) : (
              <Badge tone="warn">Not answered</Badge>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/** The isolation the application declares (0031): kind, reason, planned period, Isolation Certificate. */
export function IsolationView({ iso }: { iso: DraftFields['isolation'] }) {
  const span = windowSpan(iso.planned_from, iso.planned_to)
  return (
    <KeyValue
      items={[
        [
          'Kind of isolation',
          iso.kinds.length ? (
            <ul className="list-inside list-disc">
              {iso.kinds.map((k) => (
                <li key={k}>{labelFor(ISOLATION_KINDS, k)}</li>
              ))}
            </ul>
          ) : (
            'Not given'
          ),
        ],
        ['Reason for isolation', <TextBlock text={iso.reason} />],
        ['Isolation planned to be applied', iso.planned_from ? fmtDateTime(iso.planned_from) : 'Not given'],
        ['Isolation planned to be removed', iso.planned_to ? fmtDateTime(iso.planned_to) : 'Not given'],
        ['Planned isolation duration', span ? (span.ms > 0 ? span.text : 'Ends before it starts') : 'Not given'],
        [
          'Isolation Certificate (F08) completed',
          iso.certificate_documented === true
            ? `Yes - No. ${iso.certificate_reference ?? 'not given'}`
            : iso.certificate_documented === false
              ? 'Not yet - recorded and verified before authorisation'
              : 'Not answered',
        ],
      ]}
    />
  )
}

export function GasPlanView({ plan }: { plan: DraftFields['gas_plan'] }) {
  return (
    <KeyValue
      items={[
        ['Gas testing required', yesNo(plan.required)],
        [
          'Why (triggers)',
          plan.triggers.length ? (
            <ul className="list-inside list-disc">
              {plan.triggers.map((t) => (
                <li key={t}>{labelFor(GAS_TRIGGERS, t)}</li>
              ))}
            </ul>
          ) : (
            <span className="text-ink-faint">None</span>
          ),
        ],
        ['Gases to test', plan.species.length ? plan.species.map((s) => speciesLabel(GAS_SPECIES, s)).join(', ') : <span className="text-ink-faint">None</span>],
        [
          'Retest interval',
          plan.retest_interval_hours !== null ? `Every ${hoursText(plan.retest_interval_hours)}` : <span className="text-ink-faint">{plan.required ? 'Not given' : 'Not needed'}</span>,
        ],
        ['Continuous monitoring', yesNo(plan.continuous_monitoring)],
        ['Notes', <TextBlock text={plan.notes} empty="None" />],
      ]}
    />
  )
}

/** The third-party question and, on Yes, the four confirmations (GOV-004). */
export function ThirdPartyView({ tp }: { tp: DraftFields['third_party'] }) {
  return (
    <div className="space-y-2">
      <p className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="text-ink">{THIRD_PARTY_QUESTION}</span>
        {tp.involved === true ? <Badge tone="info">Yes</Badge> : tp.involved === false ? <Badge tone="neutral">No</Badge> : <Badge tone="warn">Not answered</Badge>}
      </p>
      {tp.involved === true ? (
        <ul className="divide-y divide-line rounded-lg border border-line bg-white">
          {THIRD_PARTY_CONFIRMATIONS.map((c) => (
            <li key={c.key} className="flex flex-wrap items-center justify-between gap-2 p-2 text-sm">
              <span className="text-ink">{c.label}</span>
              {tp[c.key] ? <Badge tone="ok">Confirmed</Badge> : <Badge tone="bad">Not confirmed</Badge>}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/** The SP-2329 Appendix 1 activities chosen for the class (note 20). */
export function ClassActivitiesList({ codes, sealed }: { codes: readonly string[]; sealed?: unknown }) {
  if (codes.length === 0) return <span className="text-ink-faint">None chosen</span>
  return (
    <ul className="list-inside list-disc">
      {classActivityLabels(codes, sealed).map((l, i) => (
        <li key={i}>{l}</li>
      ))}
    </ul>
  )
}

/** The four job-readiness confirmations (0015). */
export function JobReadinessView({ jr }: { jr: DraftFields['job_readiness'] }) {
  return (
    <div>
      <p className="mb-1 text-sm text-ink">{JOB_READINESS_QUESTION}:</p>
      <ul className="divide-y divide-line rounded-lg border border-line bg-white">
        {JOB_READINESS_CONFIRMATIONS.map((c) => (
          <li key={c.key} className="flex flex-wrap items-center justify-between gap-2 p-2 text-sm">
            <span className="text-ink">{c.label}</span>
            {jr[c.key] ? <Badge tone="ok">Verified and adequate</Badge> : <Badge tone="bad">Not confirmed</Badge>}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * The work-location classification the applicant chose (0015). A revision sealed before
 * 0015 has none and shows the site-register snapshot it was sealed with.
 */
export function AreaClassificationView({ code, label, register, draft }: { code: string | null; label: string | null; register?: object | null; draft?: boolean }) {
  if (code) {
    const known = locationClassificationLabel(code)
    return (
      <div className="rounded-lg border border-line bg-white p-3 text-sm">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-faint">The unit's work-location classification{draft ? ' (from the register)' : ' (sealed with this revision)'}</p>
        <p className="font-semibold text-ink">{label ?? known ?? code}</p>
        {!known ? <p className="mt-1 text-xs font-semibold text-bad">! Not one of the listed classifications ({code}).</p> : null}
      </div>
    )
  }
  if (draft || !register) return <p className="text-sm text-ink-faint">Not recorded for this unit in the register.</p>
  return (
    <div>
      <p className="mb-2 text-xs text-ink-soft">Sealed before units carried a work-location classification: the site-register snapshot of this revision.</p>
      <ClassificationList area={register} />
    </div>
  )
}

/**
 * Date of application (the server time the application was submitted) and date of
 * initiation (the planned start of work).
 */
export function applicationDates(submittedAt: string | null | undefined, plannedStart: string | null | undefined): Array<[ReactNode, ReactNode]> {
  return [
    ['Date of application', submittedAt ? fmtDateTime(submittedAt) : <span className="text-ink-faint">Recorded when you submit</span>],
    ['Date of initiation (planned start)', plannedStart ? fmtDateTime(plannedStart) : <span className="text-ink-faint">Not given</span>],
  ]
}

export function WindowText({ from, to }: { from: string | null | undefined; to: string | null | undefined }) {
  const span = windowSpan(from, to)
  return (
    <span>
      {fmtDateTime(from)} to {fmtDateTime(to)}
      {span ? <span className="block text-xs text-ink-soft">{span.ms > 0 ? span.text : 'The end is not after the start'}</span> : null}
    </span>
  )
}

// -----------------------------------------------------------------------------
// Whole draft, by wizard step (review step and the permit page before submission)
// -----------------------------------------------------------------------------
function SummarySection({ title, step, onEdit, children }: { title: string; step: StepKey; onEdit?: (s: StepKey) => void; children: ReactNode }) {
  return (
    <Card>
      <SectionTitle
        aside={
          onEdit ? (
            <Button variant="secondary" type="button" onClick={() => onEdit(step)}>
              Change
            </Button>
          ) : undefined
        }
      >
        {title}
      </SectionTitle>
      {children}
    </Card>
  )
}

export function DraftSummary({ fields, lookup, siteId, permitClass, onEdit }: {
  fields: DraftFields
  lookup: Lookup
  siteId: string | null
  permitClass: string | null
  onEdit?: (s: StepKey) => void
}) {
  const area = lookup.area(fields.site_area_id)
  const wt = lookup.workType(fields.work_type_id)
  const ph = lookup.person(fields.nominated_ph_person_id)
  return (
    <div className="space-y-4">
      <SummarySection title="1. Site and class" step="scope" onEdit={onEdit}>
        <KeyValue
          items={[
            ['Geographical area', lookup.siteName(siteId) ?? <span className="text-ink-faint">Not chosen</span>],
            ['Permit class', classLabel(permitClass)],
            ['Why this class: the activities in this work', <ClassActivitiesList codes={fields.class_activities} />],
            ...(fields.class_basis.trim() ? ([['Earlier class basis (free text)', <TextBlock text={fields.class_basis} />]] as Array<[ReactNode, ReactNode]>) : []),
            ['Unit', area ? `${area.name} (${area.code})` : <span className="text-ink-faint">Not chosen</span>],
            ['Unit work-location classification', area?.location_classification_label ?? locationClassificationLabel(area?.location_classification) ?? <span className="text-ink-faint">Not recorded</span>],
            ['Exact location', <TextBlock text={fields.location_detail} />],
            ['Work type', wt ? `${wt.description} - ${wt.activity_group}` : <span className="text-ink-faint">Not chosen</span>],
          ]}
        />
      </SummarySection>
      <SummarySection title="2. The work" step="work" onEdit={onEdit}>
        <KeyValue
          items={[
            ['Title', <TextBlock text={fields.title} />],
            ['Work order reference', <TextBlock text={fields.work_order_ref} empty="Not given" />],
            ...applicationDates(null, fields.requested_from),
            ['Planned window', fields.requested_from || fields.requested_to ? <WindowText from={fields.requested_from} to={fields.requested_to} /> : <span className="text-ink-faint">Not given</span>],
            ['Urgency justification', <TextBlock text={fields.urgency_justification} empty="None (enough notice)" />],
            ['What will be done', <TextBlock text={fields.work_description} />],
            ['Work boundary', <TextBlock text={fields.work_boundary} />],
            ['Tools, equipment and vehicles', <TextBlock text={fields.tools_equipment} />],
            ['Most people in the work party', fields.work_party_max !== null ? String(fields.work_party_max) : <span className="text-ink-faint">Not given</span>],
            ['Nominated Permit Holder', ph ? `${ph.display_name}${ph.organisation ? ` (${ph.organisation})` : ''}` : <span className="text-ink-faint">Not chosen</span>],
          ]}
        />
        <SubHeading>Third party or subcontractor</SubHeading>
        <ThirdPartyView tp={fields.third_party} />
      </SummarySection>
      <SummarySection title="3. Tasks" step="tasks" onEdit={onEdit}>
        {fields.tasks.length === 0 ? (
          <p className="text-sm text-ink-faint">No tasks yet.</p>
        ) : (
          <div className="space-y-2">
            {fields.tasks.map((t) => (
              <TaskCard key={t.sequence} task={taskFromDraft(t)} />
            ))}
          </div>
        )}
      </SummarySection>
      <SummarySection title="4. Hazards and Job HSE plan" step="hazards" onEdit={onEdit}>
        <SubHeading>Hazards and controls</SubHeading>
        <ControlsList items={fields.hazards} />
        <SubHeading>Job HSE plan</SubHeading>
        <JhsepView jhsep={fields.jhsep} />
        <SubHeading>Job readiness</SubHeading>
        <JobReadinessView jr={fields.job_readiness} />
        <SubHeading>SIMOPS and MOPO</SubHeading>
        <SimopsView simops={fields.simops} />
      </SummarySection>
      <SummarySection title="5. Special work, gas and documents" step="routes" onEdit={onEdit}>
        <SubHeading>Special work</SubHeading>
        <RouteAnswers fields={fields} />
        <SubHeading>Gas testing plan</SubHeading>
        <GasPlanView plan={fields.gas_plan} />
        <SubHeading>Supporting documents</SubHeading>
        {fields.supporting_documents.length === 0 ? (
          <p className="text-sm text-ink-faint">None listed.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {fields.supporting_documents.map((d, i) => (
              <li key={i}>
                <span className="font-semibold">{d.kind || '(kind not chosen)'}</span>
                {d.reason ? ` - ${d.reason}` : ''}
              </li>
            ))}
          </ul>
        )}
        <SubHeading>Other affected units</SubHeading>
        {fields.affected_area_ids.length === 0 ? (
          <p className="text-sm text-ink-faint">None.</p>
        ) : (
          <ul className="list-inside list-disc text-sm">
            {fields.affected_area_ids.map((id) => (
              <li key={id}>{lookup.area(id)?.name ?? `Unknown area (${id.slice(0, 8)})`}</li>
            ))}
          </ul>
        )}
        <SubHeading>Area classification</SubHeading>
        <AreaClassificationView code={area?.location_classification ?? null} label={area?.location_classification_label ?? null} draft />
        <p className="mt-2 text-sm">
          <span className="font-semibold">Classification confirmed by the applicant: </span>
          {fields.location_attributes_confirmed ? 'Yes' : 'No'}
        </p>
      </SummarySection>
    </div>
  )
}
